// EventsTable.js
import {
    Box, Button, Flex, IconButton, Input, Table, Tbody, Td, Text, Badge,
    Th, Thead, Tr, Tag, Spinner, useToast, Tooltip, InputGroup, InputLeftElement
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiEye, FiEdit2, FiTrash2, FiArrowUp, FiArrowDown, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import EventsService from '../../api/services/EventsService';
import DateRangeSelector from "../../components/DateRangeSelector/DateRangeSelector";
import EventsCardList from "../../components/EventsCardList/EventsCardList";
import { format } from 'date-fns';

const EventsTable = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');

    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [sort, setSort] = useState('createdAt');
    const [sortDir, setSortDir] = useState('desc');
    const toast = useToast();
    const navigate = useNavigate();

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const res = await EventsService.getAllEvents({
                page,
                search,
                dateFrom: dateFrom ? format(new Date(dateFrom), 'yyyy-MM-dd') : undefined,
                dateTo: dateTo ? format(new Date(dateTo), 'yyyy-MM-dd') : undefined,
                createdFrom: createdFrom ? format(new Date(createdFrom), 'yyyy-MM-dd') : undefined,
                createdTo: createdTo ? format(new Date(createdTo), 'yyyy-MM-dd') : undefined,
                sort: (sort + "_" + sortDir),
            });
            setEvents(res.data.data.items);
            setTotalPages(res.data.data.totalPages);
        } catch (error) {
            toast({ title: 'Błąd pobierania danych', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, [page, sort, sortDir, dateFrom, dateTo]);

    const handleSearch = () => {
        setPage(1);
        fetchEvents();
    };

    const handleSort = (column) => {
        if (sort === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column);
            setSortDir('asc');
        }
    };

    return (
        <Box w="100%" maxW="7xl" px={4} py={8}>
            <Flex justify="space-between" flexDirection={{ base: 'column', md: 'row' }} className="events-header" mb="48px">
                <Flex flexDirection={"column"} className="header-left">
                    <Text className="header-title">
                        Lista wydarzeń
                    </Text>
                    <Button leftIcon={<FiEdit2 />} bg="brand.500" color="white" borderRadius={0} _hover={{ bg: 'brand.600' }} onClick={() => navigate('/events/new')}>
                        Nowe wydarzenie
                    </Button>
                </Flex>
                <Flex flexDirection={"column"} className="header-right" gap="16px">
                    <Flex gap="16px">
                        <InputGroup>
                            <InputLeftElement pointerEvents="none">
                                <FiSearch color="#9D9D9D" />
                            </InputLeftElement>
                            <Input
                                placeholder="Nazwa wydarzenia"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                bg="white"
                                borderColor="gray4"
                                borderRadius={0}
                            />
                        </InputGroup>
                        <Button onClick={handleSearch} bg="brand.500" color="white" borderRadius={0} _hover={{ bg: 'brand.600' }} gap="10px" w="220px">
                            Wyszukaj
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 9.75L13 12.75" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M6.25 10.75C8.87335 10.75 11 8.62335 11 6C11 3.37665 8.87335 1.25 6.25 1.25C3.62665 1.25 1.5 3.37665 1.5 6C1.5 8.62335 3.62665 10.75 6.25 10.75Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </Button>
                    </Flex>
                    <Flex gap={"48px"} justifyContent={"end"}>
                        <DateRangeSelector
                            label="Data utworzenia"
                            fromDate={createdFrom}
                            toDate={createdTo}
                            onChange={({ from, to }) => {
                                setCreatedFrom(from);
                                setCreatedTo(to);
                            }}
                        />
                        <DateRangeSelector
                            label="Data rozpoczęcia"
                            fromDate={dateFrom}
                            toDate={dateTo}
                            onChange={({ from, to }) => {
                                setDateFrom(from);
                                setDateTo(to);
                            }}
                        />
                    </Flex>
                </Flex>
            </Flex>

            <EventsCardList
                events={events}
                sort={sort}
                sortDir={sortDir}
                onSort={handleSort}
                loading={loading}
            />

            {totalPages > 1 &&
                <Flex justify="center" gap={'4px'} my={'48px'}>
                    <Button bgColor={'#0A4725'} _hover={{bgColor: '#0A4725'}} borderRadius={0}
                            key={1}
                            onClick={() => setPage(1)}
                            w={'40px'} h={'40px'}
                    >
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 9L0.999999 5L5 1" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </Button>
                    {Array.from({ length: totalPages }, (_, idx) => (
                        <Button fontFamily={'Poppins'} fontWeight={'400'} fontSize={'16px'}
                                bgColor={'#E1E1E1'} _hover={{bgColor: '#E1E1E1'}} borderRadius={0}
                                borderColor={'#0A4725'} border={page === idx + 1 ? "2px" : "0"}
                                key={idx + 1}
                                onClick={() => setPage(idx + 1)}
                                w={'40px'} h={'40px'}
                        >
                            {idx + 1}
                        </Button>
                    ))}
                    <Button bgColor={'#0A4725'} _hover={{bgColor: '#0A4725'}} borderRadius={0}
                            key={totalPages}
                            onClick={() => setPage(totalPages)}
                            w={'40px'} h={'40px'}
                    >
                        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L5 5L1 9" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </Button>
                </Flex>
            }

        </Box>
    );
};

export default EventsTable;
